import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building, User, CreditCard, CheckCircle, ArrowRight, ArrowLeft, Mail, Phone, MapPin } from 'lucide-react';
import axios from 'axios';

const InstitutionSignup = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [currentStep, setCurrentStep] = useState(1);
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [formData, setFormData] = useState({
        // Step 1: Institution Details
        institutionName: '',
        institutionEmail: '',
        institutionPhone: '',
        institutionAddress: '',
        institutionCity: '',
        institutionCountry: 'Kenya',

        // Step 2: Admin Account
        adminName: '',
        adminEmail: '',
        adminPhone: '',
        adminPassword: '',
        adminConfirmPassword: '',

        // Step 3: Package Selection
        selectedPackageId: null,

        // Step 4: Campus Setup (optional)
        campusName: '',
        campusLocation: '',
        skipCampus: false
    });

    useEffect(() => {
        fetchPackages();

        // Pre-select package from URL if available
        const packageParam = searchParams.get('package');
        if (packageParam) {
            const pkg = packages.find(p => p.name === packageParam);
            if (pkg) {
                setFormData(prev => ({ ...prev, selectedPackageId: pkg.id }));
            }
        }
    }, [searchParams, packages]);

    const fetchPackages = async () => {
        try {
            const response = await axios.get('/subscriptions/packages');
            setPackages(response.data);
        } catch (error) {
            console.error('Error fetching packages:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setError('');
    };

    const validateStep = (step) => {
        switch (step) {
            case 1:
                if (!formData.institutionName || !formData.institutionEmail || !formData.institutionPhone) {
                    setError('Please fill in all required institution details');
                    return false;
                }
                if (!/\S+@\S+\.\S+/.test(formData.institutionEmail)) {
                    setError('Please enter a valid email address');
                    return false;
                }
                break;

            case 2:
                if (!formData.adminName || !formData.adminEmail || !formData.adminPassword) {
                    setError('Please fill in all required admin details');
                    return false;
                }
                if (formData.adminPassword.length < 6) {
                    setError('Password must be at least 6 characters');
                    return false;
                }
                if (formData.adminPassword !== formData.adminConfirmPassword) {
                    setError('Passwords do not match');
                    return false;
                }
                break;

            case 3:
                if (!formData.selectedPackageId) {
                    setError('Please select a subscription package');
                    return false;
                }
                break;

            case 4:
                if (!formData.skipCampus && !formData.campusName) {
                    setError('Please enter campus name or skip this step');
                    return false;
                }
                break;
        }
        return true;
    };

    const handleNext = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => prev + 1);
            setError('');
        }
    };

    const handleBack = () => {
        setCurrentStep(prev => prev - 1);
        setError('');
    };

    const handleSubmit = async () => {
        if (!validateStep(4)) return;

        setLoading(true);
        setError('');

        try {
            // Create institution and admin account
            const response = await axios.post('/institutions/register', {
                institutionName: formData.institutionName,
                institutionEmail: formData.institutionEmail,
                institutionPhone: formData.institutionPhone,
                institutionAddress: formData.institutionAddress,
                institutionCity: formData.institutionCity,
                institutionCountry: formData.institutionCountry,
                adminName: formData.adminName,
                adminEmail: formData.adminEmail,
                adminPhone: formData.adminPhone,
                adminPassword: formData.adminPassword,
                packageId: formData.selectedPackageId,
                campusName: formData.skipCampus ? null : formData.campusName,
                campusLocation: formData.skipCampus ? null : formData.campusLocation
            });

            // Show success and redirect
            alert('Institution registered successfully! Please check your email to verify your account and activate your 30-day free trial.');
            navigate('/login');
        } catch (error) {
            console.error('Signup error:', error);
            setError(error.response?.data?.message || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const steps = [
        { number: 1, title: 'Institution Details', icon: Building },
        { number: 2, title: 'Admin Account', icon: User },
        { number: 3, title: 'Choose Package', icon: CreditCard },
        { number: 4, title: 'Campus Setup', icon: CheckCircle }
    ];

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(amount);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        Register Your Institution
                    </h1>
                    <p className="text-gray-600">
                        Start your 30-day free trial today - no credit card required
                    </p>
                </div>

                {/* Progress Steps */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        {steps.map((step, index) => {
                            const Icon = step.icon;
                            const isActive = currentStep === step.number;
                            const isCompleted = currentStep > step.number;

                            return (
                                <React.Fragment key={step.number}>
                                    <div className="flex flex-col items-center flex-1">
                                        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${isCompleted ? 'bg-green-600' :
                                            isActive ? 'bg-blue-600' :
                                                'bg-gray-300'
                                            }`}>
                                            <Icon className="w-6 h-6 text-white" />
                                        </div>
                                        <p className={`text-sm font-medium ${isActive ? 'text-blue-600' :
                                            isCompleted ? 'text-green-600' :
                                                'text-gray-500'
                                            }`}>
                                            {step.title}
                                        </p>
                                    </div>
                                    {index < steps.length - 1 && (
                                        <div className={`h-1 flex-1 mx-2 ${currentStep > step.number ? 'bg-green-600' : 'bg-gray-300'
                                            }`} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>

                {/* Form Card */}
                <div className="bg-white rounded-2xl shadow-xl p-8">
                    {/* Step 1: Institution Details */}
                    {currentStep === 1 && (
                        <div className="space-y-6">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Institution Information</h2>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Institution Name *
                                </label>
                                <input
                                    type="text"
                                    name="institutionName"
                                    value={formData.institutionName}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="e.g., NPBC Theological College"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <Mail className="w-4 h-4 inline mr-1" />
                                        Institution Email *
                                    </label>
                                    <input
                                        type="email"
                                        name="institutionEmail"
                                        value={formData.institutionEmail}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="info@institution.edu"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <Phone className="w-4 h-4 inline mr-1" />
                                        Phone Number *
                                    </label>
                                    <input
                                        type="tel"
                                        name="institutionPhone"
                                        value={formData.institutionPhone}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="+254 700 000 000"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <MapPin className="w-4 h-4 inline mr-1" />
                                    Address
                                </label>
                                <input
                                    type="text"
                                    name="institutionAddress"
                                    value={formData.institutionAddress}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Street address"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        City
                                    </label>
                                    <input
                                        type="text"
                                        name="institutionCity"
                                        value={formData.institutionCity}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Nairobi"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Country
                                    </label>
                                    <input
                                        type="text"
                                        name="institutionCountry"
                                        value={formData.institutionCountry}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Admin Account */}
                    {currentStep === 2 && (
                        <div className="space-y-6">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Create Admin Account</h2>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Full Name *
                                </label>
                                <input
                                    type="text"
                                    name="adminName"
                                    value={formData.adminName}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="John Doe"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        name="adminEmail"
                                        value={formData.adminEmail}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="admin@institution.edu"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        name="adminPhone"
                                        value={formData.adminPhone}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="+254 700 000 000"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Password *
                                    </label>
                                    <input
                                        type="password"
                                        name="adminPassword"
                                        value={formData.adminPassword}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Minimum 6 characters"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Confirm Password *
                                    </label>
                                    <input
                                        type="password"
                                        name="adminConfirmPassword"
                                        value={formData.adminConfirmPassword}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Re-enter password"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Package Selection */}
                    {currentStep === 3 && (
                        <div className="space-y-6">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Choose Your Package</h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {packages.map((pkg) => (
                                    <div
                                        key={pkg.id}
                                        onClick={() => setFormData(prev => ({ ...prev, selectedPackageId: pkg.id }))}
                                        className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${formData.selectedPackageId === pkg.id
                                            ? 'border-blue-600 bg-blue-50'
                                            : 'border-gray-200 hover:border-blue-300'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-xl font-bold text-gray-900">{pkg.displayName}</h3>
                                            {formData.selectedPackageId === pkg.id && (
                                                <CheckCircle className="w-6 h-6 text-blue-600" />
                                            )}
                                        </div>

                                        <p className="text-3xl font-bold text-gray-900 mb-2">
                                            {formatCurrency(pkg.pricePerStudentPerUnit)}
                                        </p>
                                        <p className="text-sm text-gray-600 mb-4">/student/unit</p>

                                        <p className="text-gray-700 mb-4">{pkg.description}</p>

                                        <ul className="space-y-2">
                                            {pkg.features.slice(0, 5).map((feature, index) => (
                                                <li key={index} className="flex items-start text-sm text-gray-700">
                                                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                                                    {feature}
                                                </li>
                                            ))}
                                            {pkg.features.length > 5 && (
                                                <li className="text-sm text-blue-600">
                                                    +{pkg.features.length - 5} more features
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                ))}
                            </div>

                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <p className="text-sm text-green-800">
                                    <strong>🎉 30-Day Free Trial:</strong> You'll get full access to all features for 30 days. No credit card required!
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Campus Setup */}
                    {currentStep === 4 && (
                        <div className="space-y-6">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Campus Setup (Optional)</h2>

                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                <p className="text-sm text-blue-800">
                                    You can add your first campus now or skip and add campuses later from your dashboard.
                                </p>
                            </div>

                            <div className="flex items-center mb-6">
                                <input
                                    type="checkbox"
                                    name="skipCampus"
                                    checked={formData.skipCampus}
                                    onChange={handleChange}
                                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                />
                                <label className="ml-2 text-sm text-gray-700">
                                    Skip campus setup (I'll add campuses later)
                                </label>
                            </div>

                            {!formData.skipCampus && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Campus Name *
                                        </label>
                                        <input
                                            type="text"
                                            name="campusName"
                                            value={formData.campusName}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Main Campus"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Campus Location
                                        </label>
                                        <input
                                            type="text"
                                            name="campusLocation"
                                            value={formData.campusLocation}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="City or address"
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    )}

                    {/* Navigation Buttons */}
                    <div className="flex justify-between mt-8">
                        <button
                            onClick={handleBack}
                            disabled={currentStep === 1}
                            className={`flex items-center px-6 py-3 rounded-lg font-semibold transition-colors ${currentStep === 1
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            <ArrowLeft className="w-5 h-5 mr-2" />
                            Back
                        </button>

                        {currentStep < 4 ? (
                            <button
                                onClick={handleNext}
                                className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                            >
                                Next
                                <ArrowRight className="w-5 h-5 ml-2" />
                            </button>
                        ) : (
                            <button
                                onClick={handleSubmit}
                                disabled={loading}
                                className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                        Creating Account...
                                    </>
                                ) : (
                                    <>
                                        Complete Registration
                                        <CheckCircle className="w-5 h-5 ml-2" />
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="text-center mt-8">
                    <p className="text-sm text-gray-600">
                        Already have an account?{' '}
                        <button
                            onClick={() => navigate('/login')}
                            className="text-blue-600 hover:text-blue-700 font-medium"
                        >
                            Sign in here
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default InstitutionSignup;
